import type { AnalysisResult, ConsentEntry, ProposalDto } from '@bk/contracts';
import type { Procedure, ProcessContract } from '@bk/process-dsl';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, ApiRequestError, errorMessage } from '../../lib/api';
import { dateTime, humanize } from '../../lib/format';
import { navigate } from '../../lib/router';
import { useTenant, useTenantQuery } from '../../lib/useTenant';
import { Badge, Button, Card, Empty, ErrorText, PageTitle } from '../../ui';
import { ContractView } from './ContractView';

/** Numeri principali dell'evidenza, leggibili: è ciò su cui si decide. */
function EvidenceFacts({ e }: { e: Record<string, any> }) {
  const facts: string[] = [];
  if (e.chiusure !== undefined) facts.push(`${e.rifacimenti} rifacimenti su ${e.chiusure} controlli (${e.tasso_percentuale}%)`);
  if (e.concentrazione) facts.push(`concentrati su ${humanize(e.concentrazione.dimensione)} = ${e.concentrazione.valore}: ${e.concentrazione.tasso_percentuale}% contro ${e.concentrazione.tasso_resto_percentuale}% nel resto`);
  if (e.segnalazioni !== undefined) facts.push(`${e.segnalazioni} segnalazioni su ${e.compiti} compiti (${e.tasso_percentuale}%)`);
  if (e.riassegnati !== undefined) facts.push(`${e.riassegnati} riassegnati a mano su ${e.compiti} (${e.tasso_percentuale}%)`);
  if (e.senza_assegnatario !== undefined) facts.push(`${e.senza_assegnatario} su ${e.compiti} senza nessuno idoneo (${e.tasso_percentuale}%)`);
  if (e.verificabili !== undefined)
    facts.push(`provato sui dati di oggi: ${e.verificabili} controllati, ${e.eccezioni} da verificare${e.non_verificabili ? `, ${e.non_verificabili} non verificabili per dati mancanti` : ''}`);
  if (e.falsi_allarmi !== undefined) facts.push(`${e.falsi_allarmi} falsi allarmi su ${e.count} verifiche`);
  if (e.conferme_consecutive !== undefined) facts.push(`${e.conferme_consecutive} conferme consecutive senza correzioni (${e.totale} in tutto, ${e.correzioni} corrette)`);
  return (
    <div className="rounded-lg bg-black/[0.03] px-3 py-2 text-xs text-ink/70">
      <ul className="list-inside list-disc">
        {facts.map((f) => (
          <li key={f}>{f}</li>
        ))}
      </ul>
      {e.esempi?.length > 0 && (
        <ul className="mt-1 list-inside list-disc">
          {e.esempi.slice(0, 5).map((x: { rif: string; motivo: string }) => (
            <li key={x.rif}>
              {x.rif}: {x.motivo}
            </li>
          ))}
        </ul>
      )}
      {e.ultime_segnalazioni?.length > 0 && <p className="mt-1">Ultime segnalazioni: {e.ultime_segnalazioni.slice(0, 3).map((n: string) => `“${n}”`).join(' · ')}</p>}
      {e.fonte && <p className="mt-1 text-ink/45">Fonte: {e.fonte}</p>}
    </div>
  );
}

function ProposalCard({ p }: { p: ProposalDto }) {
  const { tenant, t } = useTenant();
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');
  const [showVariant, setShowVariant] = useState(false);
  const answer = useMutation({
    mutationFn: (decision: 'accetta' | 'rifiuta' | 'piu_avanti') => api<ProposalDto>(t(`/proposals/${p.id}/answer`), { body: { decision, ...(reason.trim() ? { reason: reason.trim() } : {}) } }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['tenant', tenant.id] }),
  });
  return (
    <Card>
      <div className="mb-2 flex flex-wrap items-start gap-2">
        <div className="min-w-0 flex-1">
          <p className="font-semibold">{p.title}</p>
          <p className="text-xs text-ink/50">
            {p.process_name && (
              <button className="underline-offset-2 hover:underline" onClick={() => navigate(`/t/${tenant.id}/processi/${p.process_id}`)}>
                {p.process_name}
              </button>
            )}{' '}
            · {dateTime(p.created_at)}
          </p>
        </div>
        <Badge tone={p.kind === 'autonomia' ? 'blue' : p.kind === 'integrazione' || p.kind === 'controllo' ? 'green' : 'amber'}>
          {{ autonomia: 'autonomia', integrazione: 'collegamento', controllo: 'controllo', correzione_controllo: 'correzione controllo', variante_processo: 'variante' }[p.kind]}
        </Badge>
        {p.status === 'rimandata' && <Badge>rimandata</Badge>}
      </div>
      <p className="whitespace-pre-line text-sm text-ink/80">{p.rationale}</p>
      {p.benefit && <p className="mt-2 text-sm font-medium text-emerald-800">Beneficio stimato: {p.benefit}</p>}
      <div className="mt-3">
        <EvidenceFacts e={p.evidence} />
      </div>
      {p.variant ? (
        <div className="mt-3">
          <Button variant="ghost" onClick={() => setShowVariant(!showVariant)}>
            {showVariant ? 'Nascondi il processo modificato' : 'Vedi il processo modificato'}
          </Button>
          {showVariant && (
            <div className="mt-2 rounded-lg border border-black/10 p-3">
              <ContractView contract={p.variant.contract as ProcessContract} procedure={p.variant.procedure as Procedure} />
            </div>
          )}
        </div>
      ) : (
        p.kind === 'variante_processo' && <p className="mt-3 text-xs text-ink/50">Nessuna modifica automatica del processo: è una proposta da discutere.</p>
      )}
      {p.kind === 'integrazione' && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Button variant="ghost" onClick={() => navigate(`/t/${tenant.id}/software`)}>
            Vai a Software per collegarlo
          </Button>
          <span className="text-xs text-ink/50">Accettare non collega nulla da solo: servono le credenziali, inserite da chi ha accesso al programma.</span>
        </div>
      )}
      <div className="mt-4 space-y-2">
        <input className="w-full rounded-lg border border-black/15 px-3 py-2 text-sm" placeholder="Motivo (facoltativo, viene ricordato)" value={reason} onChange={(e) => setReason(e.target.value)} />
        <div className="flex flex-wrap gap-2">
          <Button disabled={answer.isPending} onClick={() => answer.mutate('accetta')}>
            {p.kind === 'autonomia' ? 'Concedi autonomia' : p.kind === 'controllo' ? 'Sì, attiva il controllo' : p.control?.azione === 'spegni' ? 'Sì, spegnilo' : p.kind === 'correzione_controllo' ? 'Sì, correggilo' : p.variant ? 'Sì, prova in osservazione' : 'Sì'}
          </Button>
          <Button variant="ghost" disabled={answer.isPending} onClick={() => answer.mutate('piu_avanti')}>
            Più avanti
          </Button>
          <Button variant="ghost" disabled={answer.isPending} onClick={() => answer.mutate('rifiuta')}>
            No
          </Button>
        </div>
        <p className="text-xs text-ink/45">Con “No” la proposta non torna finché le evidenze non cambiano in modo sostanziale; con “Più avanti” torna tra 14 giorni.</p>
        <ErrorText>{answer.error ? errorMessage(answer.error) : ''}</ErrorText>
      </div>
    </Card>
  );
}

function ConsentCard() {
  const { tenant, t } = useTenant();
  const queryClient = useQueryClient();
  const consents = useTenantQuery<{ data: ConsentEntry[] }>(['consents'], '/consents');
  const set = useMutation({
    mutationFn: (c: { section: string; granted: boolean }) => api(t('/consents'), { method: 'PUT', body: { section: c.section, purpose: 'ottimizzazione', granted: c.granted } }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['tenant', tenant.id, 'consents'] }),
  });
  return (
    <Card>
      <h2 className="mb-1 text-sm font-semibold">Consensi per l’ottimizzazione</h2>
      <p className="mb-3 text-xs text-ink/55">Cosa il sistema può analizzare per proporre miglioramenti. L’apprendimento resta sempre dentro la tua azienda.</p>
      <ul className="space-y-3 text-sm">
        {consents.data?.data.map((c) => (
          <li key={c.section}>
            <label className="flex items-start gap-2">
              <input type="checkbox" className="mt-1" checked={c.granted} disabled={set.isPending || c.section === 'persone_presenze'} onChange={(e) => set.mutate({ section: c.section, granted: e.target.checked })} />
              <span>
                <span className="font-medium">{c.label}</span>
                <span className="block text-xs text-ink/55">{c.description}</span>
                {c.warning && <span className="mt-1 block text-xs text-amber-800">{c.warning}</span>}
              </span>
            </label>
          </li>
        ))}
      </ul>
      <ErrorText>{set.error ? errorMessage(set.error) : ''}</ErrorText>
    </Card>
  );
}

/** Proposte del sistema: nascono dalle evidenze, si accettano, si rimandano o si rifiutano (e il “no” è ricordato). */
export function Proposals() {
  const { tenant, t } = useTenant();
  const queryClient = useQueryClient();
  const proposals = useTenantQuery<{ data: ProposalDto[] }>(['proposals'], '/proposals');
  const [needsConsent, setNeedsConsent] = useState(false);
  const analyze = useMutation({
    mutationFn: () => api<AnalysisResult>(t('/learning/analyze'), { method: 'POST' }),
    onMutate: () => setNeedsConsent(false),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['tenant', tenant.id, 'proposals'] }),
    onError: (e) => setNeedsConsent(e instanceof ApiRequestError && e.body.code === 'consent_required'),
  });
  return (
    <>
      <PageTitle
        title="Proposte"
        subtitle="Il sistema osserva come si lavora e propone miglioramenti con i numeri alla mano. Nulla cambia senza la tua conferma."
        actions={
          <Button disabled={analyze.isPending} onClick={() => analyze.mutate()}>
            {analyze.isPending ? 'Analisi…' : 'Analizza ora'}
          </Button>
        }
      />
      {analyze.data && (
        <p className="mb-4 rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
          Analisi fatta: {analyze.data.created} proposte nuove{analyze.data.suppressed ? `, ${analyze.data.suppressed} già note o in attesa di evidenze nuove` : ''}.
        </p>
      )}
      {needsConsent ? (
        <p className="mb-4 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900">Per proporre miglioramenti il sistema deve poter analizzare i dati di esecuzione dei processi: attiva il consenso qui a destra e riprova.</p>
      ) : (
        <ErrorText>{analyze.error ? errorMessage(analyze.error) : ''}</ErrorText>
      )}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <div className="min-w-0 space-y-4">
          {proposals.data?.data.length === 0 && <Empty>Nessuna proposta aperta. L’analisi gira ogni mattina; servono abbastanza esecuzioni perché un fenomeno non sia un caso isolato.</Empty>}
          {proposals.data?.data.map((p) => (
            <ProposalCard key={p.id} p={p} />
          ))}
        </div>
        <div className="min-w-0">
          <ConsentCard />
        </div>
      </div>
    </>
  );
}
